package com.mencanhealth.admin.ui

import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.attributes.*
import com.mencanhealth.admin.data.AdminAppointmentService
import com.mencanhealth.admin.model.*
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock

enum class BookingStep { SELECT_TYPE, SELECT_DATE, PATIENT_INFO, QUESTIONNAIRE, PAYMENT, CONFIRMATION }

@Composable
fun BookingPage(onNavigateHome: () -> Unit) {
    var currentStep by remember { mutableStateOf(BookingStep.SELECT_TYPE) }
    var selectedType by remember { mutableStateOf<AppointmentType?>(null) }
    var selectedModality by remember { mutableStateOf<AppointmentModality?>(AppointmentModality.PRESENCIAL) }
    var selectedDate by remember { mutableStateOf("") }
    var selectedTime by remember { mutableStateOf("") }
    var patientName by remember { mutableStateOf("") }
    var patientEmail by remember { mutableStateOf("") }
    var patientWhatsApp by remember { mutableStateOf("") }
    var patientNotes by remember { mutableStateOf("") }
    var questionnaireAnswers by remember { mutableStateOf(mutableMapOf<String, String>()) }
    var paymentMethod by remember { mutableStateOf(PaymentMethod.TRANSFER) }
    var bookingComplete by remember { mutableStateOf(false) }
    var validationError by remember { mutableStateOf<String?>(null) }
    var isSubmitting by remember { mutableStateOf(false) }

    val scope = rememberCoroutineScope()
    val appointmentService = remember { AdminAppointmentService() }

    // Calendar state
    val now = remember { Clock.System.now().toEpochMilliseconds() }
    var calendarMonth by remember { mutableStateOf(currentMonthFromEpoch(now)) }
    var calendarYear by remember { mutableStateOf(currentYearFromEpoch(now)) }

    Div({ classes("booking-page") }) {
        // Header
        Div({ classes("booking-header") }) {
            A(attrs = {
                classes("back-link")
                onClick { onNavigateHome() }
            }) { Text("\u2190 Volver") }
            H1 { Text("Agendar Cita") }
        }

        // Step indicator
        Div({ classes("booking-steps") }) {
            BookingStep.entries.forEachIndexed { index, step ->
                val stepClass = when {
                    step == currentStep -> "booking-step-active"
                    step.ordinal < currentStep.ordinal -> "booking-step-completed"
                    else -> "booking-step-pending"
                }
                Div({ classes("booking-step", stepClass) }) {
                    Span({ classes("booking-step-number") }) { Text("${index + 1}") }
                    Span({ classes("booking-step-label") }) {
                        Text(
                            when (step) {
                                BookingStep.SELECT_TYPE -> "Tipo"
                                BookingStep.SELECT_DATE -> "Fecha"
                                BookingStep.PATIENT_INFO -> "Datos"
                                BookingStep.QUESTIONNAIRE -> "Cuestionario"
                                BookingStep.PAYMENT -> "Pago"
                                BookingStep.CONFIRMATION -> "Listo"
                            }
                        )
                    }
                }
            }
        }

        // Validation error
        validationError?.let { error ->
            Div({ classes("error-banner") }) { Text(error) }
        }

        // Step content
        Div({ classes("booking-content") }) {
            when (currentStep) {
                BookingStep.SELECT_TYPE -> SelectTypeStep(
                    selectedType = selectedType,
                    selectedModality = selectedModality,
                    onTypeSelected = { selectedType = it },
                    onModalitySelected = {
                        selectedModality = it
                        if (it == AppointmentModality.PRESENCIAL && selectedType == AppointmentType.EXPRESS) {
                            selectedType = null
                        }
                    }
                )
                BookingStep.SELECT_DATE -> SelectDateStep(
                    selectedDate = selectedDate,
                    selectedTime = selectedTime,
                    calendarMonth = calendarMonth,
                    calendarYear = calendarYear,
                    onMonthChange = { month, year -> calendarMonth = month; calendarYear = year },
                    onDateSelected = { selectedDate = it; selectedTime = "" },
                    onTimeSelected = { selectedTime = it }
                )
                BookingStep.PATIENT_INFO -> PatientInfoStep(
                    patientName = patientName,
                    patientEmail = patientEmail,
                    patientWhatsApp = patientWhatsApp,
                    patientNotes = patientNotes,
                    onNameChange = { patientName = it },
                    onEmailChange = { patientEmail = it },
                    onWhatsAppChange = { patientWhatsApp = it },
                    onNotesChange = { patientNotes = it }
                )
                BookingStep.QUESTIONNAIRE -> QuestionnaireStep(
                    appointmentType = selectedType!!,
                    answers = questionnaireAnswers,
                    onAnswerChange = { id, value ->
                        questionnaireAnswers = (questionnaireAnswers.toMutableMap().also { it[id] = value })
                    }
                )
                BookingStep.PAYMENT -> PaymentStep(
                    selectedType = selectedType!!,
                    selectedModality = selectedModality!!,
                    selectedDate = selectedDate,
                    selectedTime = selectedTime,
                    paymentMethod = paymentMethod,
                    isPresencial = selectedModality == AppointmentModality.PRESENCIAL,
                    isSubmitting = isSubmitting,
                    onPaymentMethodChange = { paymentMethod = it },
                    onConfirm = {
                        isSubmitting = true
                        scope.launch {
                            try {
                                val responsesJson = kotlin.js.JSON.stringify(
                                    questionnaireAnswers.entries.fold(js("({})")) { obj, (k, v) ->
                                        obj[k] = v; obj
                                    }
                                )
                                appointmentService.createAppointment(
                                    Appointment(
                                        patientName = patientName,
                                        patientEmail = patientEmail,
                                        patientWhatsApp = patientWhatsApp,
                                        appointmentType = selectedType!!.name,
                                        modality = selectedModality!!.name,
                                        date = selectedDate,
                                        timeSlot = selectedTime,
                                        durationMinutes = selectedType!!.durationMinutes,
                                        paymentMethod = paymentMethod.name,
                                        notes = patientNotes,
                                        questionnaireResponses = responsesJson,
                                        createdAt = Clock.System.now().toEpochMilliseconds()
                                    )
                                )
                                bookingComplete = true
                                currentStep = BookingStep.CONFIRMATION
                            } catch (e: Exception) {
                                validationError = "Error al registrar la cita. Intenta de nuevo."
                            } finally {
                                isSubmitting = false
                            }
                        }
                    }
                )
                BookingStep.CONFIRMATION -> ConfirmationStep(
                    selectedType = selectedType!!,
                    selectedModality = selectedModality!!,
                    selectedDate = selectedDate,
                    selectedTime = selectedTime,
                    patientName = patientName,
                    onNavigateHome = onNavigateHome
                )
            }
        }

        // Navigation buttons (not shown on confirmation)
        if (currentStep != BookingStep.CONFIRMATION) {
            Div({ classes("booking-nav") }) {
                if (currentStep != BookingStep.SELECT_TYPE) {
                    Button({
                        classes("btn-secondary")
                        onClick {
                            validationError = null
                            currentStep = BookingStep.entries[currentStep.ordinal - 1]
                        }
                    }) { Text("Anterior") }
                }

                if (currentStep != BookingStep.PAYMENT) {
                    Button({
                        classes("btn-primary")
                        onClick {
                            validationError = null
                            val error = validateStep(currentStep, selectedType, selectedModality, selectedDate, selectedTime, patientName, patientEmail, patientWhatsApp, questionnaireAnswers)
                            if (error != null) {
                                validationError = error
                            } else {
                                currentStep = BookingStep.entries[currentStep.ordinal + 1]
                            }
                        }
                    }) { Text("Siguiente") }
                }
            }
        }
    }
}

private fun validateStep(
    step: BookingStep,
    type: AppointmentType?,
    modality: AppointmentModality?,
    date: String,
    time: String,
    name: String,
    email: String,
    whatsApp: String,
    questionnaireAnswers: Map<String, String> = emptyMap()
): String? = when (step) {
    BookingStep.SELECT_TYPE -> when {
        type == null -> "Selecciona un tipo de consulta"
        modality == null -> "Selecciona una modalidad"
        type == AppointmentType.EXPRESS && modality == AppointmentModality.PRESENCIAL ->
            "La Consulta Express solo está disponible en modalidad En Línea"
        else -> null
    }
    BookingStep.SELECT_DATE -> when {
        date.isEmpty() -> "Selecciona una fecha"
        time.isEmpty() -> "Selecciona un horario"
        else -> null
    }
    BookingStep.PATIENT_INFO -> when {
        name.isBlank() -> "Ingresa tu nombre completo"
        email.isBlank() || !email.contains("@") -> "Ingresa un email válido"
        whatsApp.isBlank() -> "Ingresa tu número de WhatsApp"
        else -> null
    }
    BookingStep.QUESTIONNAIRE -> {
        if (type != null) {
            val questions = questionnaireForType(type)
            val requiredUnanswered = questions.filter { it.required }.count { questionnaireAnswers[it.id].isNullOrBlank() }
            if (requiredUnanswered > 0) "Faltan $requiredUnanswered preguntas obligatorias por contestar" else null
        } else null
    }
    else -> null
}

// --- Step 1: Select Type ---

@Composable
private fun SelectTypeStep(
    selectedType: AppointmentType?,
    selectedModality: AppointmentModality?,
    onTypeSelected: (AppointmentType) -> Unit,
    onModalitySelected: (AppointmentModality) -> Unit
) {
    H3 { Text("Modalidad") }
    Div({ classes("modality-selector") }) {
        AppointmentModality.entries.forEach { modality ->
            val btnClass = if (modality == selectedModality) "modality-btn-selected" else "modality-btn"
            Button({
                classes("modality-btn", btnClass)
                onClick { onModalitySelected(modality) }
            }) { Text(modality.label) }
        }
    }

    H3 { Text("Selecciona el tipo de consulta") }
    Div({ classes("type-selector") }) {
        val availableTypes = if (selectedModality == AppointmentModality.PRESENCIAL) {
            AppointmentType.entries.filter { it != AppointmentType.EXPRESS }
        } else {
            AppointmentType.entries.toList()
        }
        availableTypes.forEach { type ->
            val cardClass = if (type == selectedType) "type-card-selected" else "type-card"
            Div({
                classes("type-card", cardClass)
                onClick { onTypeSelected(type) }
            }) {
                H4 { Text(type.label) }
                P({ classes("type-price") }) { Text("$${type.priceMXN} MXN") }
                P({ classes("type-duration") }) { Text("${type.durationMinutes} minutos") }
            }
        }
    }
}

// --- Step 2: Select Date ---

@Composable
private fun SelectDateStep(
    selectedDate: String,
    selectedTime: String,
    calendarMonth: Int,
    calendarYear: Int,
    onMonthChange: (Int, Int) -> Unit,
    onDateSelected: (String) -> Unit,
    onTimeSelected: (String) -> Unit
) {
    H3 { Text("Selecciona fecha y hora") }

    // Calendar
    Div({ classes("booking-calendar") }) {
        // Month navigation
        Div({ classes("calendar-header") }) {
            Button({
                classes("calendar-nav-btn")
                onClick {
                    if (calendarMonth == 1) onMonthChange(12, calendarYear - 1)
                    else onMonthChange(calendarMonth - 1, calendarYear)
                }
            }) { Text("\u25C0") }
            Span({ classes("calendar-month-label") }) {
                Text("${monthName(calendarMonth)} $calendarYear")
            }
            Button({
                classes("calendar-nav-btn")
                onClick {
                    if (calendarMonth == 12) onMonthChange(1, calendarYear + 1)
                    else onMonthChange(calendarMonth + 1, calendarYear)
                }
            }) { Text("\u25B6") }
        }

        // Day headers
        Div({ classes("calendar-grid") }) {
            listOf("Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom").forEach { day ->
                Div({ classes("calendar-day-header") }) { Text(day) }
            }

            // Calendar days
            val daysInMonth = daysInMonth(calendarMonth, calendarYear)
            val firstDayOfWeek = dayOfWeek(calendarYear, calendarMonth, 1) // 0=Mon, 6=Sun

            // Empty cells before first day
            repeat(firstDayOfWeek) {
                Div({ classes("calendar-day-empty") }) {}
            }

            for (day in 1..daysInMonth) {
                val dateStr = "${calendarYear}-${calendarMonth.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"
                val dow = dayOfWeek(calendarYear, calendarMonth, day)
                val isWeekend = dow >= 5 // Saturday or Sunday
                val isSelected = dateStr == selectedDate

                val dayClass = when {
                    isSelected -> "calendar-day-selected"
                    isWeekend -> "calendar-day-disabled"
                    else -> "calendar-day-available"
                }

                Div({
                    classes("calendar-day", dayClass)
                    if (!isWeekend) {
                        onClick { onDateSelected(dateStr) }
                    }
                }) { Text("$day") }
            }
        }
    }

    // Time slots
    if (selectedDate.isNotEmpty()) {
        H4 { Text("Horarios disponibles") }
        Div({ classes("time-slots") }) {
            val slots = listOf("09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "12:00", "12:30", "16:00", "16:30", "17:00", "17:30", "18:00")
            slots.forEach { slot ->
                val slotClass = if (slot == selectedTime) "time-slot-selected" else "time-slot-available"
                Div({
                    classes("time-slot", slotClass)
                    onClick { onTimeSelected(slot) }
                }) { Text(slot) }
            }
        }
    }
}

// --- Step 3: Patient Info ---

@Composable
private fun PatientInfoStep(
    patientName: String,
    patientEmail: String,
    patientWhatsApp: String,
    patientNotes: String,
    onNameChange: (String) -> Unit,
    onEmailChange: (String) -> Unit,
    onWhatsAppChange: (String) -> Unit,
    onNotesChange: (String) -> Unit
) {
    H3 { Text("Tus datos") }
    Div({ classes("booking-form") }) {
        Div({ classes("form-group") }) {
            Span({ classes("form-label") }) { Text("Nombre completo *") }
            Input(InputType.Text) {
                classes("form-input")
                value(patientName)
                onInput { onNameChange(it.value) }
                attr("placeholder", "Tu nombre completo")
            }
        }
        Div({ classes("form-group") }) {
            Span({ classes("form-label") }) { Text("Email *") }
            Input(InputType.Email) {
                classes("form-input")
                value(patientEmail)
                onInput { onEmailChange(it.value) }
                attr("placeholder", "tu@email.com")
            }
        }
        Div({ classes("form-group") }) {
            Span({ classes("form-label") }) { Text("WhatsApp (+52) *") }
            Input(InputType.Tel) {
                classes("form-input")
                value(patientWhatsApp)
                onInput { onWhatsAppChange(it.value) }
                attr("placeholder", "+52 222 123 4567")
            }
        }
        Div({ classes("form-group") }) {
            Span({ classes("form-label") }) { Text("Notas / Motivo de consulta") }
            TextArea(attrs = {
                classes("form-input")
                attr("placeholder", "Cuéntanos brevemente el motivo de tu consulta...")
                attr("rows", "4")
                onInput { onNotesChange(it.value) }
            }, value = patientNotes)
        }
    }
}

// --- Step 4: Payment ---

@Composable
private fun PaymentStep(
    selectedType: AppointmentType,
    selectedModality: AppointmentModality,
    selectedDate: String,
    selectedTime: String,
    paymentMethod: PaymentMethod,
    isPresencial: Boolean,
    isSubmitting: Boolean,
    onPaymentMethodChange: (PaymentMethod) -> Unit,
    onConfirm: () -> Unit
) {
    H3 { Text("Resumen y pago") }

    // Booking summary
    Div({ classes("booking-summary") }) {
        Div({ classes("summary-row") }) {
            Span { Text("Consulta:") }
            Span { Text(selectedType.label) }
        }
        Div({ classes("summary-row") }) {
            Span { Text("Modalidad:") }
            Span { Text(selectedModality.label) }
        }
        Div({ classes("summary-row") }) {
            Span { Text("Fecha:") }
            Span { Text(selectedDate) }
        }
        Div({ classes("summary-row") }) {
            Span { Text("Hora:") }
            Span { Text(selectedTime) }
        }
        Div({ classes("summary-row") }) {
            Span { Text("Duración:") }
            Span { Text("${selectedType.durationMinutes} minutos") }
        }
        Div({ classes("summary-row", "summary-total") }) {
            Span { Text("Total:") }
            Span { Text("$${selectedType.priceMXN} MXN") }
        }
    }

    // Payment method
    H4 { Text("Método de pago") }
    Div({ classes("modality-selector") }) {
        Button({
            val btnClass = if (paymentMethod == PaymentMethod.TRANSFER) "modality-btn-selected" else "modality-btn"
            classes("modality-btn", btnClass)
            onClick { onPaymentMethodChange(PaymentMethod.TRANSFER) }
        }) { Text("Transferencia Bancaria") }

        if (isPresencial) {
            Button({
                val btnClass = if (paymentMethod == PaymentMethod.CASH) "modality-btn-selected" else "modality-btn"
                classes("modality-btn", btnClass)
                onClick { onPaymentMethodChange(PaymentMethod.CASH) }
            }) { Text("Efectivo") }
        }
    }

    // Bank details for transfer
    if (paymentMethod == PaymentMethod.TRANSFER) {
        Div({ classes("payment-card") }) {
            H4 { Text("Datos para transferencia") }
            Div({ classes("bank-detail") }) {
                Span({ classes("bank-label") }) { Text("Banco:") }
                Span { Text("Santander") }
            }
            Div({ classes("bank-detail") }) {
                Span({ classes("bank-label") }) { Text("CLABE:") }
                Span({ classes("bank-clabe") }) { Text("014650606073669140") }
            }
            Div({ classes("bank-detail") }) {
                Span({ classes("bank-label") }) { Text("Beneficiario:") }
                Span { Text("Luis Enrique Mendez Cantero") }
            }
            Div({ classes("bank-detail") }) {
                Span({ classes("bank-label") }) { Text("Monto:") }
                Span({ classes("bank-amount") }) { Text("$${selectedType.priceMXN} MXN") }
            }

            A(
                href = "https://wa.me/522221234567?text=Hola,%20adjunto%20mi%20comprobante%20de%20pago%20para%20mi%20cita",
                attrs = {
                    classes("payment-whatsapp")
                    attr("target", "_blank")
                    attr("rel", "noopener noreferrer")
                }
            ) { Text("Enviar comprobante por WhatsApp") }
        }
    }

    // Confirm button
    Button({
        classes("btn-primary", "btn-confirm")
        if (isSubmitting) attr("disabled", "true")
        onClick { onConfirm() }
    }) { Text(if (isSubmitting) "Registrando..." else "Confirmar Cita") }
}

// --- Step 5: Confirmation ---

@Composable
private fun ConfirmationStep(
    selectedType: AppointmentType,
    selectedModality: AppointmentModality,
    selectedDate: String,
    selectedTime: String,
    patientName: String,
    onNavigateHome: () -> Unit
) {
    Div({ classes("booking-confirmation") }) {
        Div({ classes("confirmation-icon") }) { Text("\u2705") }
        H2 { Text("\u00A1Tu cita ha sido registrada!") }
        P({ classes("confirmation-name") }) { Text("Gracias, $patientName") }

        Div({ classes("booking-summary") }) {
            Div({ classes("summary-row") }) {
                Span { Text("Consulta:") }
                Span { Text(selectedType.label) }
            }
            Div({ classes("summary-row") }) {
                Span { Text("Modalidad:") }
                Span { Text(selectedModality.label) }
            }
            Div({ classes("summary-row") }) {
                Span { Text("Fecha:") }
                Span { Text(selectedDate) }
            }
            Div({ classes("summary-row") }) {
                Span { Text("Hora:") }
                Span { Text(selectedTime) }
            }
        }

        Div({ classes("confirmation-status") }) {
            Text("Estado: Pendiente de pago — Te confirmaremos por WhatsApp")
        }

        Button({
            classes("btn-primary")
            onClick { onNavigateHome() }
        }) { Text("Volver al inicio") }
    }
}

// --- Calendar helpers ---

private fun monthName(month: Int): String = when (month) {
    1 -> "Enero"; 2 -> "Febrero"; 3 -> "Marzo"; 4 -> "Abril"
    5 -> "Mayo"; 6 -> "Junio"; 7 -> "Julio"; 8 -> "Agosto"
    9 -> "Septiembre"; 10 -> "Octubre"; 11 -> "Noviembre"; 12 -> "Diciembre"
    else -> ""
}

private fun daysInMonth(month: Int, year: Int): Int = when (month) {
    1, 3, 5, 7, 8, 10, 12 -> 31
    4, 6, 9, 11 -> 30
    2 -> if (year % 4 == 0 && (year % 100 != 0 || year % 400 == 0)) 29 else 28
    else -> 30
}

/** Returns 0=Monday .. 6=Sunday using Zeller-like formula */
private fun dayOfWeek(year: Int, month: Int, day: Int): Int {
    // Tomohiko Sakamoto's algorithm, adjusted to return 0=Mon..6=Sun
    val t = intArrayOf(0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4)
    val y = if (month < 3) year - 1 else year
    val dow = (y + y / 4 - y / 100 + y / 400 + t[month - 1] + day) % 7
    // dow: 0=Sun,1=Mon..6=Sat -> convert to 0=Mon..6=Sun
    return (dow + 6) % 7
}

private fun currentMonthFromEpoch(epochMs: Long): Int {
    // Approximate month from epoch — JS will give us real date
    val jsDate = js("new Date(epochMs)")
    return (jsDate.getMonth() as Int) + 1
}

private fun currentYearFromEpoch(epochMs: Long): Int {
    val jsDate = js("new Date(epochMs)")
    return jsDate.getFullYear() as Int
}
